/**
 * Debtor paid before attorney assignment / accept.
 *
 * Policy: if the invoice is paid in full while the demand is still unassigned
 * (INTAKE_VALIDATED, no attorney), close as PAID_BEFORE_ASSIGNMENT with
 * no per-demand fee and do not route an attorney.
 *
 * Partial payments are allowed on any recordable status, whether or not the
 * client accepts a payment plan. The demand stays open until paid in full.
 */

export const PAID_BEFORE_ASSIGNMENT = 'PAID_BEFORE_ASSIGNMENT';

export const PRE_ASSIGNMENT_STATUSES = Object.freeze(['INTAKE_VALIDATED']);

/** Statuses where the client can manually record that the debtor paid. */
export const CLIENT_RECORDABLE_PAYMENT_STATUSES = Object.freeze([
  'INTAKE_VALIDATED',
  'ATTORNEY_ASSIGNED',
  'CERTIFIED_MAIL_DISPATCHED',
  'DEMAND_DELIVERED',
  'LITIGATION_READY',
  'UNPAID_ESCALATE',
]);

export const TERMINAL_PAID_STATUSES = Object.freeze(['PAID', PAID_BEFORE_ASSIGNMENT]);

export function normalizeMatterStatus(status) {
  return String(status || '').trim().toUpperCase();
}

export function roundMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  return Math.round(amount * 100) / 100;
}

export function matterAmountOwed(matter = {}) {
  return Math.max(0, roundMoney(matter.Amount_Owed));
}

export function matterAmountPaid(matter = {}) {
  const explicit = Number(matter.Amount_Paid);
  if (Number.isFinite(explicit) && explicit >= 0) return roundMoney(explicit);
  // Legacy: fully paid demands without Amount_Paid were closed as paid in full.
  if (isPaidStatus(matter.Status)) return matterAmountOwed(matter);
  return 0;
}

export function matterBalanceRemaining(matter = {}) {
  return Math.max(0, roundMoney(matterAmountOwed(matter) - matterAmountPaid(matter)));
}

export function listMatterPaymentEntries(matter = {}) {
  const raw = matter?.Payment_Entries;
  if (Array.isArray(raw)) {
    return raw
      .map((entry, index) => normalizePaymentEntry(entry, index))
      .filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map((entry, index) => normalizePaymentEntry(entry, index))
          .filter(Boolean);
      }
    } catch {
      return [];
    }
  }
  return [];
}

function normalizePaymentEntry(entry, index = 0) {
  if (!entry || typeof entry !== 'object') return null;
  const amount = roundMoney(entry.amount ?? entry.Amount);
  if (!(amount > 0)) return null;
  const reference = String(
    entry.reference || entry.Reference || entry.referenceNumber || entry.Reference_Number || ''
  ).trim();
  return {
    id: String(entry.id || entry.ROWID || `pay_${index + 1}`),
    amount,
    currency: String(entry.currency || entry.Currency || '').trim().toUpperCase() || null,
    paidAt: entry.paidAt || entry.Paid_At || entry.createdAt || null,
    source: entry.source || entry.Paid_Source || null,
    note: entry.note || entry.Resolution_Note || null,
    reference: reference || null,
    attachments: normalizePaymentAttachments(entry),
    accountingPaymentId: String(
      entry.accountingPaymentId || entry.Accounting_Payment_ID || entry.externalPaymentId || ''
    ).trim() || null,
    accountingProvider: String(
      entry.accountingProvider || entry.Accounting_Provider || ''
    ).trim() || null,
  };
}

function normalizePaymentAttachments(entry = {}) {
  if (Array.isArray(entry.attachments)) {
    return entry.attachments
      .map((item) => (typeof item === 'string' ? item : item?.name))
      .map((name) => String(name || '').trim())
      .filter(Boolean);
  }
  if (Array.isArray(entry.proofFileNames)) {
    return entry.proofFileNames.map((name) => String(name || '').trim()).filter(Boolean);
  }
  return [];
}

/** True when an attorney has accepted / been assigned or work has progressed. */
export function isAttorneyEngaged(matter = {}) {
  const status = normalizeMatterStatus(matter.Status);
  if (matter.Attorney_ID || matter.Attorney_Name || matter.Attorney_Email) return true;
  return (
    status === 'ATTORNEY_ASSIGNED' ||
    status === 'CERTIFIED_MAIL_DISPATCHED' ||
    status === 'DEMAND_DELIVERED' ||
    status === 'PAID' ||
    status === 'LITIGATION_READY' ||
    status === 'UNPAID_ESCALATE'
  );
}

/** Eligible for no-fee auto-close when payment arrives. */
export function isEligibleForPaidBeforeAssignment(matter = {}) {
  const status = normalizeMatterStatus(matter.Status);
  if (!PRE_ASSIGNMENT_STATUSES.includes(status)) return false;
  if (matter.Attorney_ID || matter.Attorney_Name || matter.Attorney_Email) return false;
  return true;
}

/** Client can record debtor payment on New Case, Assigned, Sent, Delivered, or Unpaid. */
export function canRecordDebtorPayment(matter = {}) {
  if (!matter || isPaidStatus(matter.Status)) return false;
  if (matterBalanceRemaining(matter) <= 0) return false;
  return CLIENT_RECORDABLE_PAYMENT_STATUSES.includes(normalizeMatterStatus(matter.Status));
}

export function isPaidStatus(status) {
  return TERMINAL_PAID_STATUSES.includes(normalizeMatterStatus(status));
}

export function isPaidBeforeAssignment(status) {
  return normalizeMatterStatus(status) === PAID_BEFORE_ASSIGNMENT;
}

/**
 * Whether the per-demand fee ($249 / quoted) should be billed for this matter.
 * Waived when closed as paid before assignment.
 */
export function shouldChargeDemandFee(matter = {}) {
  if (isPaidBeforeAssignment(matter.Status)) return false;
  if (matter.Demand_Fee_Charged === false || matter.Demand_Fee_Waived === true) return false;
  if (normalizeMatterStatus(matter.Demand_Fee_Waived_Reason) === 'PAID_BEFORE_ASSIGNMENT') {
    return false;
  }
  // Fee locks on attorney accept; pre-accept open matters are not yet billable.
  if (isEligibleForPaidBeforeAssignment(matter)) return false;
  return isAttorneyEngaged(matter);
}

function formatMoneyMessage(value, currencyOrMatter = 'USD') {
  const amount = roundMoney(value);
  const currency =
    currencyOrMatter && typeof currencyOrMatter === 'object'
      ? String(currencyOrMatter.Currency || currencyOrMatter.currency || 'USD')
          .trim()
          .toUpperCase() || 'USD'
      : String(currencyOrMatter || 'USD').trim().toUpperCase() || 'USD';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/**
 * Resolve a debtor payment against the current matter.
 * Pass `amount` for partial or full payments. Omitting amount pays the remaining balance.
 * @returns {{
 *   patch: object,
 *   waived: boolean,
 *   status: string,
 *   message: string,
 *   fullyPaid: boolean,
 *   payment: object,
 *   amountPaid: number,
 *   balanceRemaining: number
 * }}
 */
export function resolveDebtorPayment(matter = {}, extras = {}) {
  const now = extras.paidAt || new Date().toISOString();
  const source = extras.source || null;
  const owed = matterAmountOwed(matter);
  const previouslyPaid = matterAmountPaid(matter);
  const remainingBefore = Math.max(0, roundMoney(owed - previouslyPaid));

  if (!(owed > 0)) {
    const err = new Error('This demand has no amount owed to collect.');
    err.status = 400;
    throw err;
  }
  if (!(remainingBefore > 0)) {
    const err = new Error('This demand is already paid in full.');
    err.status = 400;
    throw err;
  }

  let amount =
    extras.amount == null || extras.amount === ''
      ? remainingBefore
      : roundMoney(extras.amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    const err = new Error('Enter a payment amount greater than zero.');
    err.status = 400;
    throw err;
  }
  if (amount > remainingBefore) {
    const err = new Error(
      `Payment cannot exceed the remaining balance of ${formatMoneyMessage(remainingBefore, matter)}.`
    );
    err.status = 400;
    throw err;
  }

  const nextPaid = roundMoney(previouslyPaid + amount);
  const balanceRemaining = Math.max(0, roundMoney(owed - nextPaid));
  const fullyPaid = balanceRemaining <= 0;
  const payment = {
    id: extras.paymentId || `pay_${Date.now()}`,
    amount,
    currency: String(matter.Currency || matter.currency || extras.currency || 'USD')
      .trim()
      .toUpperCase() || 'USD',
    paidAt: now,
    source,
    note: extras.note || null,
    reference: String(extras.reference || extras.referenceNumber || '').trim() || null,
    attachments: Array.isArray(extras.attachments)
      ? extras.attachments.map((name) => String(name || '').trim()).filter(Boolean)
      : [],
    accountingPaymentId: extras.accountingPaymentId || null,
    accountingProvider: extras.accountingProvider || null,
  };
  const paymentEntries = [...listMatterPaymentEntries(matter), payment];
  const statusBeforePaid = normalizeMatterStatus(matter.Status) || matter.Status;

  if (!fullyPaid) {
    const patch = {
      Amount_Paid: nextPaid,
      Payment_Entries: paymentEntries,
      Paid_Source: source,
      Updated_At: now,
    };
    return {
      patch,
      waived: false,
      status: normalizeMatterStatus(matter.Status) || matter.Status,
      fullyPaid: false,
      payment,
      amountPaid: amount,
      balanceRemaining,
      message: `Partial payment of ${formatMoneyMessage(amount, matter)} recorded. ${formatMoneyMessage(
        balanceRemaining,
        matter
      )} remaining.`,
    };
  }

  if (isEligibleForPaidBeforeAssignment(matter)) {
    const patch = {
      Status: PAID_BEFORE_ASSIGNMENT,
      Status_Before_Paid: statusBeforePaid,
      Amount_Paid: owed,
      Payment_Entries: paymentEntries,
      Paid_At: now,
      Paid_Source: source,
      Demand_Fee_Charged: false,
      Demand_Fee_Amount: 0,
      Demand_Fee_Waived: true,
      Demand_Fee_Waived_Reason: 'paid_before_assignment',
      Resolution_Note:
        previouslyPaid > 0
          ? 'Final payment recorded before attorney assignment. Matter closed with no per-demand fee.'
          : 'Debtor paid before attorney assignment. Matter closed with no per-demand fee.',
      Updated_At: now,
    };
    return {
      patch,
      waived: true,
      status: PAID_BEFORE_ASSIGNMENT,
      fullyPaid: true,
      payment,
      amountPaid: amount,
      balanceRemaining: 0,
      message:
        previouslyPaid > 0
          ? `Final payment of ${formatMoneyMessage(amount, matter)} recorded. Demand closed as paid before assignment — no per-demand fee.`
          : 'Payment recorded. Demand closed as paid before assignment — no per-demand fee.',
    };
  }

  const patch = {
    Status: 'PAID',
    Status_Before_Paid: statusBeforePaid,
    Amount_Paid: owed,
    Payment_Entries: paymentEntries,
    Paid_At: now,
    Paid_Source: source,
    Demand_Fee_Waived: false,
    Resolution_Note: extras.resolutionNote || null,
    Updated_At: now,
  };
  return {
    patch,
    waived: false,
    status: 'PAID',
    fullyPaid: true,
    payment,
    amountPaid: amount,
    balanceRemaining: 0,
    message:
      previouslyPaid > 0
        ? `Final payment of ${formatMoneyMessage(amount, matter)} recorded. Demand marked paid.`
        : 'Payment recorded. Demand marked paid.',
  };
}

/** Stamp an accounting payment id onto a local payment entry after sync. */
export function attachAccountingToPayment(entries, paymentId, accounting = {}) {
  const localId = String(paymentId || '').trim();
  const accountingPaymentId = String(accounting?.paymentId || '').trim();
  if (!localId || !accountingPaymentId) {
    return Array.isArray(entries) ? entries.map((entry) => ({ ...entry })) : [];
  }
  const provider = String(accounting.provider || '').trim() || null;
  return (entries || []).map((entry) => {
    if (String(entry?.id) !== localId) return { ...entry };
    return {
      ...entry,
      accountingPaymentId,
      accountingProvider: provider,
    };
  });
}

function reopenStatusAfterPaymentRemoval(matter = {}) {
  const prior = normalizeMatterStatus(matter.Status_Before_Paid);
  if (prior && !isPaidStatus(prior)) return prior;
  if (isPaidBeforeAssignment(matter.Status)) return 'INTAKE_VALIDATED';
  if (matter.Attorney_ID || matter.Attorney_Name || matter.Attorney_Email) {
    return 'ATTORNEY_ASSIGNED';
  }
  return 'INTAKE_VALIDATED';
}

/**
 * Remove one payment entry and recalculate Amount_Paid / paid status.
 * Reopens the demand when deleting leaves an unpaid balance on a paid matter.
 */
export function removeDebtorPayment(matter = {}, paymentId, extras = {}) {
  const now = extras.removedAt || new Date().toISOString();
  const id = String(paymentId || '').trim();
  if (!id) {
    const err = new Error('Payment id required.');
    err.status = 400;
    throw err;
  }

  const entries = listMatterPaymentEntries(matter);
  const removed = entries.find((entry) => entry.id === id) || null;
  if (!removed) {
    const err = new Error('Payment entry not found.');
    err.status = 404;
    throw err;
  }

  const paymentEntries = entries.filter((entry) => entry.id !== id);
  const nextPaid = roundMoney(paymentEntries.reduce((sum, entry) => sum + entry.amount, 0));
  const owed = matterAmountOwed(matter);
  const balanceRemaining = Math.max(0, roundMoney(owed - nextPaid));
  const wasPaid = isPaidStatus(matter.Status);
  const stillFullyPaid = owed > 0 && balanceRemaining <= 0 && nextPaid > 0;

  let status = normalizeMatterStatus(matter.Status) || matter.Status;
  let reopened = false;
  let patch = {
    Amount_Paid: nextPaid,
    Payment_Entries: paymentEntries,
    Updated_At: now,
  };

  if (wasPaid && !stillFullyPaid) {
    reopened = true;
    status = reopenStatusAfterPaymentRemoval(matter);
    patch = {
      ...patch,
      Status: status,
      Paid_At: null,
      Paid_Source: paymentEntries.length ? matter.Paid_Source || null : null,
      Status_Before_Paid: null,
    };
    if (
      isPaidBeforeAssignment(matter.Status) ||
      String(matter.Demand_Fee_Waived_Reason || '').toLowerCase() === 'paid_before_assignment'
    ) {
      patch.Demand_Fee_Waived = false;
      patch.Demand_Fee_Waived_Reason = null;
      patch.Demand_Fee_Charged = false;
      patch.Demand_Fee_Amount = null;
      patch.Resolution_Note = null;
    }
  }

  return {
    patch,
    removed,
    status,
    reopened,
    fullyPaid: stillFullyPaid,
    amountPaid: nextPaid,
    amountRemoved: removed.amount,
    balanceRemaining,
    message: reopened
      ? `Payment of ${formatMoneyMessage(removed.amount, matter)} deleted. Demand reopened with ${formatMoneyMessage(
          balanceRemaining,
          matter
        )} remaining.`
      : paymentEntries.length
        ? `Payment of ${formatMoneyMessage(removed.amount, matter)} deleted.${
            balanceRemaining > 0
              ? ` ${formatMoneyMessage(balanceRemaining, matter)} remaining.`
              : ''
          }`
        : `Payment of ${formatMoneyMessage(removed.amount, matter)} deleted.`,
  };
}

export function paidBeforeAssignmentClientMessage() {
  return 'If the invoice is paid before an attorney accepts the demand, the matter closes with no per-demand fee.';
}
